import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import {useCallback, useEffect, useMemo} from 'react'
import PhoneInput from 'react-phone-input-2'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {addressTypeOptions, addressTypes} from '../../address/AddressConstant'
import {getListGenericAddress} from '../../address/AddressEndPoint'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'

const PATH = 'address'

const CountryPath = '/getCountries'
const StatePath = '/getStates'
const CityPath = '/getCities'

const CountryQueryKey = 'Countries-AddressForm'
const StateQueryKey = 'States-AddressForm'
const CityQueryKey = 'Cities-AddressForm'

const StyledAddressInformationUtils = ({
    data = {},
    childrenClassName,
    shouldTextBold = false,
    isLock = true,
    onChange = () => {},
}) => {
    useSignals()
    const {user} = useAuth()

    const infoData = useMemo(() => {
        if (!data || !data?.id) {
            return {}
        }

        return data
    }, [data])

    const formData = useSignal(
        infoData && Object.keys(infoData)?.length ? {...infoData} : {},
    )
    const refCountry = useSignal()
    const refState = useSignal()
    const switchInputKeys = useSignal([])

    const queryClient = useQueryClient()

    const {
        translateKeys,
        translateCommons,
        translateOptions,
        translateAddressOptions,
    } = TranslateUtils()

    const {mutate: clearCityOptions} = useMutation(() => {}, {
        onSuccess: () => {
            queryClient.setQueryData(
                [
                    CityQueryKey,
                    refCountry.value,
                    refState.value,
                    switchInputKeys.value,
                ],
                () => [],
            )
        },
    })

    const handleSwitchInput = useCallback(
        key => {
            if (switchInputKeys.value?.includes(key)) {
                let onChangeValue = {}
                const switchInputKeysValue = switchInputKeys.value.filter(
                    x => x !== key,
                )

                switchInputKeys.value = switchInputKeysValue

                if (key === 'refState') {
                    refState.value = null

                    onChangeValue = {
                        ...formData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {
                            inputFields: [...switchInputKeysValue],
                        },
                    }

                    clearCityOptions()
                } else if (key === 'city') {
                    onChangeValue = {
                        ...formData.value,
                        city: null,
                        addressConfig: {
                            inputFields: [...switchInputKeysValue],
                        },
                    }
                }
                onChange(onChangeValue)
                formData.value = onChangeValue
            } else {
                let onChangeValue = {}
                if (
                    key === 'refState' &&
                    !switchInputKeys.value.includes('city')
                ) {
                    const switchInputKeyValue = [
                        ...switchInputKeys.value,
                        key,
                        'city',
                    ]

                    onChangeValue = {
                        ...formData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {
                            inputFields: switchInputKeyValue,
                        },
                    }

                    switchInputKeys.value = switchInputKeyValue
                } else if (key === 'city') {
                    switchInputKeys.value = [...switchInputKeys.value, key]

                    onChangeValue = {
                        ...formData.value,
                        city: null,
                        addressConfig: {
                            inputFields: [...switchInputKeys.value, key],
                        },
                    }
                } else {
                    switchInputKeys.value = [...switchInputKeys.value, key]

                    onChangeValue = {
                        ...formData.value,
                        addressConfig: {
                            inputFields: [...switchInputKeys.value, key],
                        },
                    }
                }

                onChange(onChangeValue)
                formData.value = onChangeValue
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [switchInputKeys, onChange, formData, clearCityOptions],
    )

    useEffect(() => {
        if (!!data) {
            const refStateValue =
                data?.addressConfig &&
                data.addressConfig?.inputFields?.length > 0 &&
                data.addressConfig?.inputFields?.includes('refState')
                    ? data?.state
                    : data?.refState

            formData.value = {
                ...data,
                refState: refStateValue,
            }
            switchInputKeys.value =
                data?.addressConfig && data.addressConfig?.inputFields
                    ? [...data?.addressConfig.inputFields]
                    : []
            refCountry.value = data && data.refCountry ? data.refCountry : ''
            refState.value = refStateValue
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const {data: countryOptions} = useQuery(
        [CountryQueryKey],
        async () => await getAddressData(PATH + CountryPath, user?.id),
        {
            refetchOnMount: false,
        },
    )

    const {data: stateOptions} = useQuery(
        [StateQueryKey, refCountry.value, switchInputKeys.value],
        async () => await getAddressData(PATH + StatePath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !switchInputKeys.value.includes('refState'),
            refetchOnMount: false,
        },
    )

    const {data: cityOptions} = useQuery(
        [CityQueryKey, refCountry.value, refState.value, switchInputKeys.value],
        async () => await getAddressData(PATH + CityPath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !!refState.value &&
                !switchInputKeys.value.includes('refState') &&
                !switchInputKeys.value.includes('city'),
            refetchOnMount: false,
        },
    )

    const getAddressData = async (path, userId) => {
        return await getListGenericAddress(
            path,
            userId,
            refCountry.value,
            refState.value,
        )
    }

    const addressList = useMemo(() => {
        return [
            {
                key: 'type',
                label: 'Address Type',
                placeholder: translateKeys('Optional'),
                component: component.dropdown,
                options: translateOptions(addressTypeOptions),
                defaultValue: addressTypes.UNKNOWN.value,
            },
            {
                key: 'refCountry',
                label: 'Country',
                placeholder: translateKeys('Optional'),
                component: component.dropdown,
                options: translateAddressOptions(countryOptions, 'country'),
            },
            {
                key: 'refState',
                label: 'State',
                placeholder: translateKeys('Optional'),
                allowInput: true,
                component: component.dropdown,
                options: translateAddressOptions(stateOptions, 'state'),
            },
            {
                key: 'city',
                label: 'City',
                allowInput: true,
                placeholder: translateKeys('Optional'),
                component: component.dropdown,
                options: translateAddressOptions(cityOptions, 'city'),
            },
            {
                key: 'addressLine1',
                label: 'Address Line 1',
                placeholder: translateKeys('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine2',
                label: 'Address Line 2',
                placeholder: translateKeys('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine3',
                label: 'Address Line 3',
                placeholder: translateKeys('Optional'),
                component: component.inputText,
            },
            {
                key: 'addressLine4',
                label: 'Address Line 4',
                placeholder: translateKeys('Optional'),
                component: component.inputText,
            },
            {
                key: 'postalCode',
                label: 'Postal Code',
                placeholder: translateKeys('Optional'),
                component: component.inputText,
            },
            {
                key: 'phone',
                label: 'Phone',
                component: component.phoneInput,
            },
        ]
    }, [
        translateKeys,
        translateOptions,
        translateAddressOptions,
        countryOptions,
        stateOptions,
        cityOptions,
    ])

    const handleDropdownChange = useCallback(
        (key, value) => {
            if (data) {
                if (key === 'refCountry') {
                    refCountry.value = value
                    refState.value = null
                    onChange({
                        ...formData.value,
                        [key]: value,
                        refState: null,
                        city: null,
                    })
                    clearCityOptions()
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                        refState: null,
                        city: null,
                    }
                } else if (key === 'refState') {
                    refState.value = value

                    let stateLabel =
                        stateOptions &&
                        stateOptions?.length &&
                        stateOptions?.find(item => item?.value === data)?.label

                    onChange({
                        ...formData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? value
                            : stateLabel,
                        city: null,
                    })
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? value
                            : stateLabel,
                        city: null,
                    }
                } else {
                    onChange({...formData.value, [key]: value})
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                    }
                }
            }
        },
        [
            data,
            refCountry,
            refState,
            onChange,
            formData,
            clearCityOptions,
            stateOptions,
            switchInputKeys.value,
        ],
    )

    const renderItem = () => {
        return (
            <div className="grid px-4">
                {addressList?.map((item, index) => (
                    <div
                        key={item.key}
                        className={`flex col-12 md:col-6 ${
                            Math.floor(index / 2) % 2 === 0 ? 'bg-gray-50' : ''
                        } p-0 `}>
                        {/* Label */}
                        <div className="flex justify-content-between align-items-center col-3">
                            <h6 className="text-600 mb-2 font-bold">
                                {item?.label}
                            </h6>
                        </div>

                        <div className="col-9">
                            {/* Dropdown */}
                            {item.component === component.dropdown &&
                                !switchInputKeys.value?.includes(item?.key) && (
                                    <div className="flex align-items-center">
                                        <Dropdown
                                            className={`w-full h-3rem border-none focus:outline-none focus:shadow-none
                                
                            ${shouldTextBold ? 'font-semibold' : ''}
                            ${
                                Math.floor(index / 2) % 2 === 0
                                    ? 'bg-gray-50'
                                    : 'bg-white'
                            }
                            `}
                                            inputId={item.key}
                                            options={item.options || []}
                                            optionLabel="label"
                                            optionValue="value"
                                            filter
                                            value={
                                                formData.value?.[item.key] || ''
                                            }
                                            onChange={e =>
                                                handleDropdownChange(
                                                    item.key,
                                                    e.value,
                                                )
                                            }
                                            placeholder={translateKeys(
                                                item.placeholder,
                                            )}
                                        />
                                        {item?.allowInput && (
                                            <div className="flex-none align-items-center justify-content-between p-0">
                                                <Button
                                                    icon={
                                                        switchInputKeys.value?.includes(
                                                            item?.key,
                                                        )
                                                            ? 'pi pi-list'
                                                            : 'pi pi-pencil'
                                                    }
                                                    className="p-button-plain p-button-rounded bg-black-alpha-90 border-black-alpha-90"
                                                    onClick={() =>
                                                        handleSwitchInput(
                                                            item?.key,
                                                        )
                                                    }
                                                    tooltip={
                                                        switchInputKeys.value?.includes(
                                                            item?.key,
                                                        )
                                                            ? translateCommons(
                                                                  'Switch to dropdown',
                                                              )
                                                            : translateCommons(
                                                                  'Switch to input',
                                                              )
                                                    }
                                                    tooltipOptions={{
                                                        position: 'top',
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                            {/* InputText / InputNumber */}
                            {(item.component === component.inputText ||
                                item.component === component.inputNumber ||
                                switchInputKeys.value?.includes(item?.key)) && (
                                <div className="flex gap-2 align-items-center">
                                    <InputText
                                        className={`w-full h-3rem border-none focus:outline-none focus:shadow-none
                                            ${
                                                Math.floor(index / 2) % 2 === 0
                                                    ? 'bg-gray-50'
                                                    : 'bg-white'
                                            }
                                            ${
                                                shouldTextBold
                                                    ? 'font-semibold'
                                                    : 'bg-white'
                                            }`}
                                        value={formData.value?.[item.key] || ''}
                                        onChange={e =>
                                            handleDropdownChange(
                                                item.key,
                                                e.target.value,
                                            )
                                        }
                                    />
                                    {item?.allowInput && (
                                        <div className="flex-none align-items-center justify-content-between p-0">
                                            <Button
                                                icon={
                                                    switchInputKeys.value?.includes(
                                                        item?.key,
                                                    )
                                                        ? 'pi pi-list'
                                                        : 'pi pi-pencil'
                                                }
                                                className="p-button-plain p-button-rounded bg-black-alpha-90 border-black-alpha-90"
                                                onClick={() =>
                                                    handleSwitchInput(item?.key)
                                                }
                                                tooltip={
                                                    switchInputKeys.value?.includes(
                                                        item?.key,
                                                    )
                                                        ? translateCommons(
                                                              'Switch to dropdown',
                                                          )
                                                        : translateCommons(
                                                              'Switch to input',
                                                          )
                                                }
                                                tooltipOptions={{
                                                    position: 'top',
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* PhoneInput */}
                            {item.component === component.phoneInput && (
                                <PhoneInput
                                    containerClass="w-full h-3rem"
                                    inputClass="w-full h-full focus:outline-none bg-white"
                                    inputStyle={{
                                        background: 'white',
                                        border: '1px solid #ccc',
                                        boxShadow: 'none',
                                    }}
                                    country="gb"
                                    value={formData.value?.[item.key] || ''}
                                    onChange={(
                                        value,
                                        country,
                                        e,
                                        formattedData,
                                    ) => {
                                        handleDropdownChange(
                                            item.key,
                                            formattedData,
                                        )
                                        if (item.key === 'phone') {
                                            handleDropdownChange(
                                                'phoneExtension',
                                                {
                                                    countryCode:
                                                        country?.countryCode,
                                                    dialCode: country?.dialCode,
                                                },
                                            )
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return <>{renderItem()}</>
}

export default StyledAddressInformationUtils
